<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      :footer-props="footerProps"
      :options="dataTableOptions"
      :mobile-breakpoint="null"
      :search="search"
      :loading="loading"
      class="elevation-2 table__desing-general"
    >
      <template #[`item.id`]="{ index }">
        {{ index + 1 }}
      </template>

      <!-- Materials -->
      <template v-if="!hideMaterialsTable" #[`item.nameMaterial`]="{ item }">
        <div class="text-left">
          {{ item.nameMaterial }}
        </div>
      </template>
      <!-- Families -->
      <template v-else #[`item.nameFamily`]="{ item }">
        <div class="text-left">
          <a href="#" @click="$emit('getMaterials', item.MVGR4)">
            ({{ item.MVGR4 }}) {{ item.nameFamily }}</a
          >
        </div>
      </template>

      <!-- Amounts -->
      <template #[`item.accumulated`]="{ item }">
        <span :class="isNegative(item.accumulated)"
          >{{ formatOption(item.accumulated) }}%</span
        >
      </template>

      <template #[`item.difference`]="{ item }">
        <span :class="isNegative(item.difference)">
          ${{ formatOption(item.difference) }}
        </span>
      </template>

      <template #[`item.average`]="{ item }">
        <span :class="isNegative(item.average)">
          ${{ formatOption(item.average) }}
        </span>
      </template>
      <!-- End Amounts -->

      <!-- Renglonaje -->
      <template #[`item.rengTotal`]="{ item }">
        <span :class="isNegative(item.rengTotal)">
          {{ formatOption(item.rengTotal) }}
        </span>
      </template>

      <template #[`item.acumVsTotal`]="{ item }">
        <span :class="isNegative(item.acumVsTotal)">
          {{ formatOption(item.acumVsTotal) }}%
        </span>
      </template>

      <template #[`item.acumVsCurrent`]="{ item }">
        <span :class="isNegative(item.acumVsCurrent)">
          {{ formatOption(item.acumVsCurrent) }}%
        </span>
      </template>
      <!-- End Renglonaje -->

      <template v-for="index in 13" #[`item.col${index}`]="{ item }">
        <!-- Families -->
        <span
          v-if="hideMaterialsTable"
          :key="index"
          :class="getItemTextColorClass(item[`col${index}`], item, 13, 'col')"
          >{{ dollarSign }}{{ formatOption(item['col' + index]) }}</span
        >
        <!-- Materials -->
        <a
          v-else
          :key="index + '-M'"
          href="#"
          class="text-dark"
          @click="selectMaterial(item, itemsDates[index - 1])"
        >
          <span
            :class="getItemTextColorClass(item[`col${index}`], item, 13, 'col')"
          >
            {{ dollarSign }}{{ formatOption(item['col' + index]) }}
          </span>
        </a>
      </template>

      <template v-if="items.length > 0" #header>
        <tfoot>
          <tr>
            <td></td>
            <td>Total</td>
            <template v-if="option !== 'renglonaje'">
              <td>{{ sumByColumns['accumulated'] }}%</td>
              <td>${{ sumByColumns['difference'] }}</td>
              <td>${{ sumByColumns['average'] }}</td>
            </template>
            <template v-else>
              <td v-if="hideMaterialsTable">
                {{ dollarSign }}{{ sumByColumns['rengTotal'] }}
              </td>
              <td v-if="hideMaterialsTable">
                {{ sumByColumns['acumVsTotal'] }}%
              </td>
              <td>{{ sumByColumns['acumVsCurrent'] }}%</td>
            </template>
            <td v-for="i in 13" :key="i">
              {{ dollarSign }}{{ sumByColumns[`col${i}`] }}
            </td>
          </tr>
        </tfoot>
      </template>
    </v-data-table>

    <!-- Modal Details -->
    <renglonaje-details-modal
      v-model="showModal"
      :material="selectedMaterial"
    />
  </div>
</template>

<script>
  import { mapActions } from 'vuex';
  import moment from 'moment';
  import Service from '@/mixins/service.js';
  import RenglonajeDetailsModal from './RenglonajeDetailsModal.vue';
  import highlightByValue from '@/mixins/highlightByValue';
  import numbersFormats from '@/mixins/numbersFormats';
  export default {
    components: { RenglonajeDetailsModal },
    mixins: [Service, highlightByValue, numbersFormats],
    props: {
      filteredItems: {
        type: Object,
        required: true,
      },
      option: {
        type: String,
        default: 'renglonaje',
      },
      selectedYear: {
        type: Number,
        default: moment().year(),
      },
      search: {
        type: String,
        required: true,
      },
      hideMaterialsTable: Boolean,
      loading: Boolean,
    },
    data() {
      return {
        items: [],
        footerProps: {
          itemsPerPageOptions: [10, 14, 24, 34, { text: 'Todas', value: -1 }],
        },
        dataTableOptions: {
          itemsPerPage: 24,
        },
        showModal: false,
        selectedMaterial: null,
      };
    },
    computed: {
      itemsDates() {
        let currentMonth = moment().format('M');
        return this.calcularUltimosMeses(13, currentMonth, this.selectedYear);
      },
      colsHeaders() {
        return this.itemsDates.map((item, index) => ({
          text: item.date,
          value: `col${index + 1}`,
          align: 'center',
        }));
      },
      sumByColumns() {
        const totals = {};
        const columns =
          this.option === 'amounts'
            ? ['accumulated', 'difference', 'average']
            : ['rengTotal', 'acumVsTotal', 'acumVsCurrent'];

        for (const column of columns) {
          const sum = this.items.reduce(
            (total, item) => total + (Number(item[column]) || 0),
            0
          );
          totals[column] = this.formatNumberOptionalDecimals(sum, '', 0);
        }

        for (let i = 1; i <= 13; i++) {
          const sumCols = this.items.reduce((sum, item) => {
            const value = Number(item[`col${i}`]) || 0;
            return sum + value;
          }, 0);
          totals[`col${i}`] = this.formatNumberOptionalDecimals(sumCols, '', 0);
        }

        return totals;
      },
      dollarSign() {
        return this.option === 'amounts' ? '$' : '';
      },
      headers() {
        const nameColumn = !this.hideMaterialsTable
          ? { text: 'Códigos', value: 'nameMaterial', align: 'center' }
          : { text: 'Familia', value: 'nameFamily', align: 'center' };

        return [
          ...(this.option !== 'renglonaje'
            ? this.getHeadersAmounts(nameColumn)
            : this.getHeaderRenglonaje(nameColumn)),
          ...this.colsHeaders,
        ];
      },
    },
    watch: {
      option() {
        this.setItems();
      },
      filteredItems() {
        this.setItems();
      },
      selectedYear() {
        this.setItems();
      },
    },
    methods: {
      setItems() {
        const colCal1 = { amounts: 'PORACUM', renglonaje: 'RENGLO' };
        const colCal2 = { amounts: 'accumDifferenceM', renglonaje: 'ACUMTOT' };
        const colCal3 = { amounts: 'accumAverageM', renglonaje: 'PORACUR' };
        const mapCol = { amounts: 'MONTO', renglonaje: 'RENGLONAJE' };

        this.items = this.filteredItems.map((item) => {
          const config = !this.hideMaterialsTable
            ? {
                fixed: {
                  nameMaterial: `${item.MATNR} ${item.ARKTX}`,
                  MATNR: item.MATNR,
                  ARKTX: item.ARKTX,
                },
                details: item.MATDET?.MATDET ?? [],
              }
            : {
                fixed: { nameFamily: item.BEZEI, MVGR4: item.MVGR4 },
                details: item.DIVDET?.DIVDET ?? [],
              };

          const result = {
            ...config.fixed,
          };

          if (this.option !== 'renglonaje') {
            result.accumulated = item[colCal1[this.option]];
            result.difference = item[colCal2[this.option]];
            result.average = item[colCal3[this.option]];
          } else {
            result.rengTotal = item[colCal1[this.option]];
            result.acumVsTotal = item[colCal2[this.option]];
            result.acumVsCurrent = item[colCal3[this.option]];
          }

          for (let i = 0; i < 13; i++) {
            const row = config.details[i];
            result[`col${i + 1}`] = row ? row[mapCol[this.option]] : 0;
          }

          return result;
        });
        this.generateExcel();
      },
      selectMaterial(material, date) {
        this.showModal = true;
        this.selectedMaterial = { material, date };
      },
      generateExcel() {
        const hide = this.hideMaterialsTable;
        const option = this.option === 'renglonaje';
        const baseIndex = !hide && option ? 2 : 4;

        const result = this.items.map((item, index) => {
          let newRow = {
            [this.headers[0].text]: index + 1,
            [this.headers[1].text]: hide ? item.nameFamily : item.nameMaterial,
          };

          if (hide || !option) {
            newRow[this.headers[2].text] = option
              ? this.formatNumberOptionalDecimals(item.rengTotal, '')
              : `${this.formatNumberOptionalDecimals(item.accumulated, '')}%`;
            newRow[this.headers[3].text] = option
              ? `${this.formatNumberOptionalDecimals(item.acumVsTotal, '')}%`
              : this.formatNumberOptionalDecimals(item.difference);
          }

          newRow[this.headers[baseIndex].text] = option
            ? `${this.formatNumberOptionalDecimals(item.acumVsCurrent, '')}%`
            : this.formatNumberOptionalDecimals(item.average);

          for (let i = 1; i <= 13; i++) {
            newRow[this.headers[baseIndex + i].text] =
              this.formatNumberOptionalDecimals(
                item[`col${i}`],
                !option ? '$' : ''
              );
          }

          return newRow;
        });

        this.sendToExcel({
          name: `renglonaje`,
          items: result,
          showLabels: true,
        });
      },
      formatOption(value) {
        return this.formatNumberOptionalDecimals(value, '', 0);
      },
      getHeadersAmounts(nameColumn) {
        const currentYear = this.selectedYear;
        return [
          { text: 'N°', value: 'id', align: 'center' },
          nameColumn,
          {
            text: `Acumulado ${currentYear - 1} vs ${currentYear}`,
            value: 'accumulated',
            align: 'center',
          },
          {
            text: 'Diferencia acumulada',
            value: 'difference',
            align: 'center',
          },
          { text: 'Prom. mensual acum.', value: 'average', align: 'center' },
        ];
      },
      getHeaderRenglonaje(nameColumn) {
        const currentYear = this.selectedYear;
        const columnsOnlyByFamily = this.hideMaterialsTable
          ? [
              {
                text: `Renglonaje total familia`,
                value: 'rengTotal',
                align: 'center',
              },
              {
                text: `Acumulado ${currentYear} vs Total`,
                value: 'acumVsTotal',
                align: 'center',
              },
            ]
          : [];

        return [
          { text: 'N°', value: 'id', align: 'center' },
          nameColumn,
          ...columnsOnlyByFamily,
          {
            text: `Acumulado ${currentYear - 1} vs ${currentYear}`,
            value: 'acumVsCurrent',
            align: 'center',
          },
        ];
      },
      ...mapActions({
        sendToExcel: 'printer/sendToExcel',
        sendToPDF: 'printer/sendToPDF',
      }),
    },
  };
</script>

<style scoped>
  ::v-deep .v-data-table-header th.sortable .v-data-table-header__icon {
    display: block;
    text-align: center;
  }
</style>
